/**
 * useOwnerInvitations Hook
 *
 * Custom hook for fetching pending owner invitations for a specific gym.
 * Used in gym settings to manage co-owner invitations.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { OwnerInvitation, InvitationStatus } from '@flash/shared';

/**
 * Convert Firestore document to OwnerInvitation type
 */
const docToOwnerInvitation = (docId: string, docData: DocumentData): OwnerInvitation => {
  return {
    id: docId,
    gymId: docData['gymId'] as string,
    email: docData['email'] as string,
    invitedBy: docData['invitedBy'] as string,
    status: docData['status'] as InvitationStatus,
    token: docData['token'] as string,
    createdAt: docData['createdAt'],
    expiresAt: docData['expiresAt'],
  };
};

/**
 * Options for useOwnerInvitations hook
 */
interface UseOwnerInvitationsOptions {
  /** Filter by status (default: 'pending') */
  status?: InvitationStatus;
  /** Whether to use real-time listener (default: true) */
  realtime?: boolean;
}

/**
 * State interface for owner invitations query
 */
interface UseOwnerInvitationsState {
  /** List of owner invitations */
  invitations: OwnerInvitation[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useOwnerInvitations hook
 */
interface UseOwnerInvitationsResult extends UseOwnerInvitationsState {
  /** Refetch invitations from Firestore */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch owner invitations for a specific gym
 *
 * @param gymId - The gym ID to fetch invitations for (null to skip)
 * @param options - Optional configuration for filtering
 * @returns Object containing invitations, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { invitations, loading, error } = useOwnerInvitations(gymId);
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 *
 * return (
 *   <div>
 *     <h3>Pending Invitations ({invitations.length})</h3>
 *     {invitations.map(inv => (
 *       <InvitationCard key={inv.id} invitation={inv} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useOwnerInvitations = (
  gymId: string | null,
  options: UseOwnerInvitationsOptions = {}
): UseOwnerInvitationsResult => {
  const { status = 'pending', realtime = true } = options;

  const [state, setState] = useState<UseOwnerInvitationsState>({
    invitations: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch invitations from Firestore (one-time)
   */
  const fetchInvitations = useCallback(async (): Promise<void> => {
    if (!gymId) {
      setState({ invitations: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();
      const invitationsRef = collection(db, 'ownerInvitations');
      const invitationsQuery = query(
        invitationsRef,
        where('gymId', '==', gymId),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(invitationsQuery);

      const invitations: OwnerInvitation[] = snapshot.docs.map((docSnapshot) =>
        docToOwnerInvitation(docSnapshot.id, docSnapshot.data())
      );

      setState({ invitations, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch owner invitations';
      console.error('Error fetching owner invitations:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [gymId, status]);

  // Real-time subscription effect
  useEffect(() => {
    if (!gymId) {
      setState({ invitations: [], loading: false, error: null });
      return;
    }

    // For non-realtime mode, just fetch once
    if (!realtime) {
      fetchInvitations();
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const invitationsRef = collection(db, 'ownerInvitations');
    const invitationsQuery = query(
      invitationsRef,
      where('gymId', '==', gymId),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      invitationsQuery,
      (snapshot) => {
        const invitations: OwnerInvitation[] = snapshot.docs.map((docSnapshot) =>
          docToOwnerInvitation(docSnapshot.id, docSnapshot.data())
        );

        setState({ invitations, loading: false, error: null });
      },
      (err) => {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch owner invitations';
        console.error('Error in owner invitations subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [gymId, status, realtime, fetchInvitations]);

  return {
    ...state,
    refetch: fetchInvitations,
  };
};
